import React from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import {
  BarList,
  EmptyState,
  SortHeader,
  ToolbarButton,
  type BarListRow,
  type SortDirection,
} from '../../../ui-kit';
import { fetchAgentBreakdown } from '../../../services/manualDashboardService';
import { formatValue } from '../format';
import type { AgentBreakdownPayload, PeriodDef } from '../../../types/manualDashboard';

/** Below a third of the middle agent's count, a figure rests on too little to
 *  read like the others. Greyed rather than dropped — the same rule as the
 *  Scorecard tab. */
const THIN_RATIO = 3;

type SortKey = 'value' | 'denominator';

interface AgentBreakdownProps {
  dashboardId: string;
  metricKey: string;
  /** The table's cohorts, oldest first; the breakdown can step between them. */
  periods: PeriodDef[];
  periodKey: string;
  onPeriodChange: (periodKey: string) => void;
  onClose: () => void;
  filters: Record<string, string[]>;
}

/**
 * One figure in a cohort table, split by agent: the leads that arrived in one
 * cohort, each agent counted against their own.
 */
export default function AgentBreakdown({
  dashboardId, metricKey, periods, periodKey, onPeriodChange, onClose, filters,
}: AgentBreakdownProps) {
  const period = periods.find((p) => p.key === periodKey);
  const [data, setData] = React.useState<AgentBreakdownPayload | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  // Most leads first: ranked by the figure, whoever had three leads tops it.
  const [sort, setSort] = React.useState<{ key: SortKey; direction: SortDirection }>({
    key: 'denominator', direction: 'desc',
  });
  const filtersKey = JSON.stringify(filters);

  React.useEffect(() => {
    if (!period) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchAgentBreakdown(
      dashboardId,
      { metric: metricKey, start: period.start, end: period.end, filters: JSON.parse(filtersKey) },
      controller.signal,
    )
      .then(setData)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [dashboardId, metricKey, period, filtersKey]);

  const agents = data?.agents ?? [];
  const counts = agents.map((a) => a.denominator).sort((a, b) => a - b);
  const middle = counts.length ? counts[Math.floor(counts.length / 2)] : 0;
  const thinBelow = middle / THIN_RATIO;
  const thinCount = agents.filter((a) => a.denominator < thinBelow).length;
  const denominatorLabel = data?.denominator_label ?? 'count';
  const format = data?.metric.format ?? 'int';

  const rows: BarListRow[] = [...agents]
    .sort((a, b) => {
      const av = (sort.key === 'value' ? a.value : a.denominator) ?? -Infinity;
      const bv = (sort.key === 'value' ? b.value : b.denominator) ?? -Infinity;
      return sort.direction === 'desc' ? bv - av : av - bv;
    })
    .map((a) => {
      const thin = a.denominator < thinBelow;
      return {
        key: a.agent,
        label: a.agent,
        value: a.value,
        secondary: a.denominator,
        muted: thin,
        hint: thin
          ? `${a.agent}: only ${Math.round(a.denominator).toLocaleString('en-US')} ${denominatorLabel}, too few for this figure to be reliable. Greyed out, not hidden.`
          : undefined,
      };
    });

  const toggle = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, direction: s.direction === 'desc' ? 'asc' : 'desc' } : { key, direction: 'desc' }));

  // Step between cohorts in the order the table reads them: left is the
  // cohort to this one's left in the table, which is the newer one.
  const shown = periods.findIndex((p) => p.key === periodKey);
  const step = (by: number) => {
    const next = periods[shown + by];
    if (next) onPeriodChange(next.key);
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2" sx={{ mr: 'auto' }}>
          {data?.metric.label ?? 'Agent breakdown'} by agent
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <ToolbarButton
            variant="ghost"
            icon={<ChevronLeftRounded fontSize="small" />}
            onClick={() => step(1)}
            disabled={shown >= periods.length - 1}
            aria-label="Newer cohort"
            tooltip="Newer cohort"
          />
          <Typography variant="body2" sx={(t) => ({ minWidth: 118, textAlign: 'center', color: t.tokens.text.secondary })}>
            Leads arrived{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>{period?.label ?? ''}</Box>
          </Typography>
          <ToolbarButton
            variant="ghost"
            icon={<ChevronRightRounded fontSize="small" />}
            onClick={() => step(-1)}
            disabled={shown <= 0}
            aria-label="Older cohort"
            tooltip="Older cohort"
          />
        </Stack>
        <ToolbarButton
          variant="ghost"
          icon={<CloseRounded fontSize="small" />}
          onClick={onClose}
          aria-label="Close agent breakdown"
          tooltip="Close"
        />
      </Stack>

      {data && (data.note || data.metric_note) && (
        <Typography variant="caption" sx={(t) => ({ display: 'block', color: t.tokens.text.secondary, maxWidth: '90ch' })}>
          {[data.note, data.metric_note].filter(Boolean).join(' ')}
        </Typography>
      )}

      {error ? (
        <EmptyState tone="error" message={error} height={120} />
      ) : loading && !data ? (
        <Skeleton variant="rounded" height={160} />
      ) : (
        <BarList
          rows={rows}
          formatValue={(v) => formatValue(v, format)}
          valueLabel={data?.metric.label ?? ''}
          labelHeading={`${agents.length} agent${agents.length === 1 ? '' : 's'}`}
          secondaryLabel={denominatorLabel}
          formatSecondary={(v) => Math.round(v).toLocaleString('en-US')}
          valueHeading={
            <SortHeader
              label={data?.metric.label ?? 'Value'}
              direction={sort.key === 'value' ? sort.direction : null}
              onClick={() => toggle('value')}
            />
          }
          secondaryHeading={
            <SortHeader
              label={denominatorLabel}
              direction={sort.key === 'denominator' ? sort.direction : null}
              onClick={() => toggle('denominator')}
              numeric
            />
          }
          emptyMessage="No sales agent worked these leads."
        />
      )}

      {thinCount > 0 && !loading && (
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
          {thinCount === 1 ? '1 agent is' : `${thinCount} agents are`} greyed out: their figure rests on far
          fewer {denominatorLabel} than the rest, so one or two outcomes swing it. Nothing is hidden.
        </Typography>
      )}
    </Stack>
  );
}
